import { fetchAuthSession } from "aws-amplify/auth";
import type { AuthSession } from "@aws-amplify/core";
import * as Sentry from "@sentry/react";

const EXPIRY_BUFFER_SECONDS = 300;

let cachedSession: AuthSession | null = null;
let inFlightPromise: Promise<AuthSession> | null = null;

function isTokenExpiringSoon(session: AuthSession): boolean {
  const exp = session.tokens?.accessToken.payload.exp;
  if (!exp) {
    return true;
  }
  const nowInSeconds = Math.floor(Date.now() / 1000);
  return exp - nowInSeconds <= EXPIRY_BUFFER_SECONDS;
}

async function refreshSession(): Promise<AuthSession> {
  if (inFlightPromise) {
    return inFlightPromise;
  }

  inFlightPromise = (async () => {
    try {
      const session = await fetchAuthSession();
      cachedSession = session;
      inFlightPromise = null;
      return session;
    } catch (error) {
      inFlightPromise = null;
      cachedSession = null;
      Sentry.captureException(error);
      throw error;
    }
  })();

  return inFlightPromise;
}

function getAccessToken(session: AuthSession): string {
  const token = session.tokens?.accessToken.toString();
  if (!token) {
    throw new Error("No access token available in auth session");
  }
  return token;
}

export async function getCachedAccessToken(): Promise<string> {
  if (cachedSession && !isTokenExpiringSoon(cachedSession)) {
    return getAccessToken(cachedSession);
  }

  const session = await refreshSession();
  return getAccessToken(session);
}
